const COOKIES = [
  {
    name: "Supabase auth session",
    purpose: "Maintains your authenticated session across page loads.",
    provider: "legacylensai.com (via Supabase)",
    service: "Authentication and session management",
    expires: "Session / persistent (per browser settings)",
  },
  {
    name: "Cloudflare security cookies",
    purpose:
      "Used by our CDN provider Cloudflare for bot management, DDoS protection, and security verification (e.g., __cf_bm, cf_clearance).",
    provider: "legacylensai.com (via Cloudflare)",
    service: "Security and bot mitigation",
    expires: "Up to 30 minutes (__cf_bm) or 30 days (cf_clearance)",
  },
  {
    name: "Sentry error monitoring identifier",
    purpose:
      "Anonymous session identifier used solely to group related errors during a single session for debugging purposes. No personal data, no cross-site tracking.",
    provider: "legacylensai.com (via Sentry)",
    service: "Error monitoring and debugging",
    expires: "Session",
  },
];

export default function CookiePolicy() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-foreground">
      <header className="mb-12 border-b border-border pb-8">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          Cookie Policy
        </h1>
        <p className="text-base text-muted-foreground">
          Last updated May 2, 2026
        </p>
      </header>

      <section className="mb-16">
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            This Cookie Policy explains how LegacyLens AI, Inc. ("Company," "we,"
            "us," and "our") uses cookies and similar technologies to recognize
            you when you visit our website at{" "}
            <a
              href="https://legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              https://legacylensai.com
            </a>{" "}
            ("Website"). It explains what these technologies are and why we use
            them, as well as your rights to control our use of them.
          </p>
          <p>
            In some cases we may use cookies to collect personal information, or
            that becomes personal information if we combine it with other
            information.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          What are cookies?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Cookies are small data files that are placed on your computer or
            mobile device when you visit a website. Cookies are widely used by
            website owners in order to make their websites work, or to work more
            efficiently, as well as to provide reporting information.
          </p>
          <p>
            Cookies set by the website owner (in this case, LegacyLens AI, Inc.)
            are called "first-party cookies." Cookies set by parties other than
            the website owner are called "third-party cookies." Third-party
            cookies enable third-party features or functionality to be provided
            on or through the website (e.g., interactive content and security
            services). The parties that set these third-party cookies can
            recognize your computer both when it visits the website in question
            and also when it visits certain other websites.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Why do we use cookies?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            LegacyLens uses only strictly necessary cookies. These are cookies
            that are required for technical reasons in order for our Website to
            operate, including user authentication, session management, and
            security. We do not use cookies for advertising, analytics,
            marketing, or behavioral tracking. We do not allow third parties to
            serve advertising cookies through our Website.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          How can I control cookies?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Because LegacyLens uses only strictly necessary cookies for
            authentication and security, we do not provide a cookie consent
            banner or preference center. These cookies cannot be disabled
            without breaking core functionality such as login and account
            access.
          </p>
          <p>
            You can, however, control cookies through your web browser's
            settings — see the links below for instructions on the most popular
            browsers. Please note that blocking strictly necessary cookies will
            prevent you from using parts of our Website that require
            authentication.
          </p>
          <p>
            The specific cookies served through our Website are described below.
            Please note that the specific cookies served may vary over time as
            our infrastructure evolves.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">
          Strictly necessary cookies
        </h3>
        <p className="text-lg leading-relaxed mb-6">
          These cookies are essential to providing you with services available
          through our Website and to enable you to use certain features.
          Without these cookies, services such as user authentication and
          account access cannot be provided.
        </p>
        <ul className="space-y-6 list-none pl-0">
          {COOKIES.map((c) => (
            <li
              key={c.name}
              className="border border-border rounded-lg p-5 space-y-2 text-base leading-relaxed"
            >
              <div>
                <span className="font-semibold">Name:</span> {c.name}
              </div>
              <div>
                <span className="font-semibold">Purpose:</span> {c.purpose}
              </div>
              <div>
                <span className="font-semibold">Provider:</span> {c.provider}
              </div>
              <div>
                <span className="font-semibold">Service:</span> {c.service}
              </div>
              <div>
                <span className="font-semibold">Expires in:</span> {c.expires}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          How can I control cookies on my browser?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            As the means by which you can refuse cookies through your web
            browser controls vary from browser to browser, you should visit
            your browser's help menu for more information. The following is
            information about how to manage cookies on the most popular
            browsers:
          </p>
        </div>
        <ul className="mt-5 space-y-2 text-lg leading-relaxed list-disc pl-6">
          <li>Chrome</li>
          <li>Firefox</li>
          <li>Safari</li>
          <li>Edge</li>
          <li>Opera</li>
        </ul>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          What about other tracking technologies, like web beacons?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            LegacyLens does not currently use web beacons, tracking pixels, or
            similar technologies on our Website or in our transactional emails.
            Should this change in the future, we will update this Cookie Policy
            and provide notice to our users where required by law.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Do you serve targeted advertising?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            No. LegacyLens does not serve targeted advertising. We do not use
            cookies, web beacons, or other tracking technologies for
            advertising purposes, and we do not allow third parties to serve
            advertisements through our Website. We do not sell or share
            personal information with advertisers.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          How often will you update this Cookie Policy?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We may update this Cookie Policy from time to time in order to
            reflect, for example, changes to the cookies we use or for other
            operational, legal, or regulatory reasons. Please therefore revisit
            this Cookie Policy regularly to stay informed about our use of
            cookies and related technologies.
          </p>
          <p>
            The date at the top of this Cookie Policy indicates when it was
            last updated.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Where can I get further information?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            If you have any questions about our use of cookies or other
            technologies, please email us at{" "}
            <a
              href="mailto:privacy@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              privacy@legacylensai.com
            </a>{" "}
            or by post to:
          </p>
        </div>
        <address className="mt-6 not-italic font-semibold text-lg leading-relaxed">
          <div>LegacyLens AI, Inc.</div>
          <div>447 Broadway, 2nd Floor, #1240</div>
          <div>New York, NY 10013</div>
          <div>United States</div>
          <div>Phone: (+1) 646-389-1263</div>
        </address>
      </section>
    </main>
  );
}
